import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Link2, Search, FileText, Globe } from "lucide-react";
import SectionHeading from "@/components/marketing/SectionHeading";
import CTASection from "@/components/marketing/CTASection";

const NICHES = [
  { name: "Fintech & Banking", sites: "120+ sites", da: "DA 40–90" },
  { name: "Cryptocurrency & Web3", sites: "80+ sites", da: "DA 35–85" },
  { name: "Technology & SaaS", sites: "150+ sites", da: "DA 40–92" },
  { name: "Real Estate & Property", sites: "60+ sites", da: "DA 35–75" },
  { name: "Luxury & Lifestyle", sites: "45+ sites", da: "DA 40–80" },
  { name: "Business & Finance", sites: "100+ sites", da: "DA 45–90" },
];

const PROCESS = [
  { icon: Search, title: "Niche Selection", description: "Choose your industry vertical. We match you with relevant, high-authority sites in your niche." },
  { icon: FileText, title: "Content Creation", description: "Our editorial team writes SEO-optimised content tailored to the target publication's style and audience." },
  { icon: Globe, title: "Placement & Indexing", description: "Content is placed on the selected site with your brand attribution and dofollow backlinks. Permanently indexed." },
  { icon: Link2, title: "Link Reporting", description: "Receive live URLs, DA metrics, and indexing confirmation for every placement." },
];

export default function GuestPosting() {
  return (
    <div className="pt-24 lg:pt-32 bg-canvas">
      <Container>
        <div className="mb-20">
          <span className="meta-tag inline-block mb-4">Guest Posting</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[52px] font-bold text-bone leading-tight mb-6 max-w-3xl">
            High-Authority Backlinks From Niche Publications
          </h1>
          <p className="text-steel text-lg max-w-2xl mb-8">
            Build domain authority and search visibility with editorially placed guest posts on relevant, high-DA sites. Every placement includes a permanent dofollow backlink to your site.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-cobalt hover:bg-cobalt/90 text-white">
              Request a Quote <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        {/* Process */}
        <SectionHeading tag="Process" title="How Guest Posting Works" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {PROCESS.map((step, i) => (
            <div key={step.title} className="bg-slate/50 border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-2xl font-bold text-cobalt/30">{String(i + 1).padStart(2, "0")}</span>
                <step.icon className="w-5 h-5 text-cobalt" />
              </div>
              <h3 className="font-heading text-lg font-bold text-bone mb-2">{step.title}</h3>
              <p className="text-sm text-steel leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        {/* Niches */}
        <SectionHeading tag="Categories" title="Available Niches" subtitle="Select from our network of industry-specific publications." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
          {NICHES.map((niche) => (
            <div key={niche.name} className="bg-slate/30 border border-border rounded-lg px-5 py-4 hover:border-cobalt/30 transition-colors">
              <h4 className="font-heading text-lg font-semibold text-bone mb-2">{niche.name}</h4>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-cobalt">{niche.sites}</span>
                <span className="text-border">·</span>
                <span className="font-mono text-xs text-steel">{niche.da}</span>
              </div>
            </div>
          ))}
        </div>
      </Container>
      <CTASection />
    </div>
  );
}